import { LoanProprosal } from '../components/addi-product-widget/addi-product-widget.d';
import {
  ANNUAL_INTEREST_RATE,
  BR_PUBLIC_API_URL,
  BR_PUBLIC_API_URL_SANDBOX,
  BR_PUBLIC_API_URL_STG,
  BR_CHANNELS_PUBLIC_API,
  BR_CHANNELS_PUBLIC_API_SANDBOX,
  BR_CHANNELS_PUBLIC_API_STG,
  BR_EMBEDDED_BASE_URL,
  BR_EMBEDDED_BASE_URL_SANDBOX,
  BR_EMBEDDED_BASE_URL_STG,
  CO_PUBLIC_API_URL,
  CO_PUBLIC_API_URL_SANDBOX,
  CO_PUBLIC_API_URL_STG,
  CO_CHANNELS_PUBLIC_API,
  CO_CHANNELS_PUBLIC_API_SANDBOX,
  CO_CHANNELS_PUBLIC_API_STG,
  CO_EMBEDDED_BASE_URL,
  CO_EMBEDDED_BASE_URL_SANDBOX,
  CO_EMBEDDED_BASE_URL_STG,
  CO_EMBEDDED_LOCAL_URL,
  CO_PREAPPROVAL_URL,
  CO_PREAPPROVAL_URL_SANDBOX,
  CO_PREAPPROVAL_URL_STG,
  DEFAULT_WIDGET_VERSION,
  GUARANTEE_RATE,
  ORIGINATIONS_SERVER_STATUS,
  LOCAL_TEST_ENVIRONMENT,
  SANDBOX_TEST_ENVIRONMENT,
  STAGING_TEST_ENVIRONMENT,
  BR_EMBEDDED_LOCAL_URL,
  CO_ORIGINATIONS_URL,
  CO_ORIGINATIONS_URL_LOCAL,
  CO_ORIGINATIONS_URL_SANDBOX,
  BR_ORIGINATIONS_URL_SANDBOX,
  BR_ORIGINATIONS_URL_LOCAL,
  BR_ORIGINATIONS_URL_STG,
  CO_ORIGINATIONS_URL_STG,
  BR_ORIGINATIONS_URL,
  TERM_PAGO,
  CO_PREAPPROVAL_URL_LOCAL,
  GRANDE_THRESHOLD,
  TERM_INTRO,
  TERM_GRANDE,
} from '../constants';

/**
 * [1] Browser
 * [2] CustomElements
 * [3] HTTP
 * [4] Bussiness
 * [5] Numeric
 * [6] Functionals
 */

/**
 *
 * Browser
 *
 */

export const isSamsungBrowser = (): boolean => {
  return !!navigator.userAgent.match(/SamsungBrowser/);
};

export const getCookie = (name: string): string => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) return parts.pop().split(';').shift();
};

/**
 *
 * HTTP
 *
 */

export function isOkStatus(status: number): boolean {
  return ORIGINATIONS_SERVER_STATUS.some((x) => x === status);
}

export function getLocationOrigin(): string {
  return window.location.origin;
}

function getHostName(): string {
  return window.location.hostname;
}

export function getChannelsPublicAPI() {
  return SANDBOX_TEST_ENVIRONMENT.includes(getHostName())
    ? CO_CHANNELS_PUBLIC_API_SANDBOX
    : STAGING_TEST_ENVIRONMENT.includes(getHostName())
    ? CO_CHANNELS_PUBLIC_API_STG
    : CO_CHANNELS_PUBLIC_API;
}

export function getBrazilChannelsPublicAPI() {
  return SANDBOX_TEST_ENVIRONMENT.includes(getHostName())
    ? BR_CHANNELS_PUBLIC_API_SANDBOX
    : STAGING_TEST_ENVIRONMENT.includes(getHostName())
    ? BR_CHANNELS_PUBLIC_API_STG
    : BR_CHANNELS_PUBLIC_API;
}

export function getPublicAPI() {
  return SANDBOX_TEST_ENVIRONMENT.includes(getHostName())
    ? CO_PUBLIC_API_URL_SANDBOX
    : STAGING_TEST_ENVIRONMENT.includes(getHostName())
    ? CO_PUBLIC_API_URL_STG
    : CO_PUBLIC_API_URL;
}

export function getBrazilPublicAPI() {
  return SANDBOX_TEST_ENVIRONMENT.includes(getHostName())
    ? BR_PUBLIC_API_URL_SANDBOX
    : STAGING_TEST_ENVIRONMENT.includes(getHostName())
    ? BR_PUBLIC_API_URL_STG
    : BR_PUBLIC_API_URL;
}

export function getEmbeddedBaseURL(allySlug: string) {
  if (LOCAL_TEST_ENVIRONMENT.includes(getHostName()))
    return CO_EMBEDDED_LOCAL_URL;

  if (SANDBOX_TEST_ENVIRONMENT.includes(getHostName()) && !allySlug)
    return CO_EMBEDDED_BASE_URL_SANDBOX;

  if (STAGING_TEST_ENVIRONMENT.includes(getHostName()))
    return CO_EMBEDDED_BASE_URL_STG;

  return CO_EMBEDDED_BASE_URL;
}

export function getBrazilEmbeddedBaseURL() {
  if (LOCAL_TEST_ENVIRONMENT.includes(getHostName()))
    return BR_EMBEDDED_LOCAL_URL;

  if (SANDBOX_TEST_ENVIRONMENT.includes(getHostName()))
    return BR_EMBEDDED_BASE_URL_SANDBOX;

  if (STAGING_TEST_ENVIRONMENT.includes(getHostName()))
    return BR_EMBEDDED_BASE_URL_STG;

  return BR_EMBEDDED_BASE_URL;
}

export function getOriginationsBaseURL() {
  if (LOCAL_TEST_ENVIRONMENT.includes(getHostName()))
    return CO_ORIGINATIONS_URL_LOCAL;

  if (SANDBOX_TEST_ENVIRONMENT.includes(getHostName()))
    return CO_ORIGINATIONS_URL_SANDBOX;

  if (STAGING_TEST_ENVIRONMENT.includes(getHostName()))
    return CO_ORIGINATIONS_URL_STG;

  return CO_ORIGINATIONS_URL;
}

export function getBrazilOriginationsBaseURL() {
  if (LOCAL_TEST_ENVIRONMENT.includes(getHostName()))
    return BR_ORIGINATIONS_URL_LOCAL;

  if (SANDBOX_TEST_ENVIRONMENT.includes(getHostName()))
    return BR_ORIGINATIONS_URL_SANDBOX;

  if (STAGING_TEST_ENVIRONMENT.includes(getHostName()))
    return BR_ORIGINATIONS_URL_STG;

  return BR_ORIGINATIONS_URL;
}

// Please kill me when you can!!
export function getPreapprovalURL(allySlug) {
  if (LOCAL_TEST_ENVIRONMENT.includes(getHostName()))
    return CO_PREAPPROVAL_URL_LOCAL;

  if (SANDBOX_TEST_ENVIRONMENT.includes(getHostName()) && !allySlug)
    return CO_PREAPPROVAL_URL_SANDBOX;

  if (STAGING_TEST_ENVIRONMENT.includes(getHostName()))
    return CO_PREAPPROVAL_URL_STG;

  return CO_PREAPPROVAL_URL;
}

export function getRequestConfig() {
  return LOCAL_TEST_ENVIRONMENT.includes(getHostName())
    ? { timeout: 1000 }
    : {};
}

/**
 *
 * Bussiness
 *
 */

function getRequestedAmountWithDiscount(
  requestedAmount: number,
  discount: number
): number {
  return requestedAmount - requestedAmount * discount;
}

function calculatePmt(loanAmount: number, term: number): number {
  const monthlyInterestRate =
    Math.pow(1 + ANNUAL_INTEREST_RATE / 100, 1 / 12) - 1;
  const pvif = Math.pow(1 + monthlyInterestRate, term);
  const monthlyValue =
    (loanAmount * monthlyInterestRate * pvif) / (pvif - 1) +
    (loanAmount * (GUARANTEE_RATE / 100)) / term;

  return Math.round(monthlyValue);
}

export function getDefaultPagoLoanProposal(
  requestedAmount: number,
  discount: number
): LoanProprosal {
  const loanAmount = discount
    ? Math.round(getRequestedAmountWithDiscount(requestedAmount, discount))
    : requestedAmount;
  const loanFee = Math.round(loanAmount / TERM_PAGO);

  return {
    requestedAmount: loanAmount,
    term: TERM_PAGO,
    totalInstallment: loanFee,
  };
}

export function getDefaultLoanProposal(loanAmount: number): LoanProprosal {
  const term = loanAmount < GRANDE_THRESHOLD ? TERM_INTRO : TERM_GRANDE;
  const loanFee = calculatePmt(loanAmount, term);

  return {
    requestedAmount: loanAmount,
    term: term,
    effectiveAnnualRate: ANNUAL_INTEREST_RATE,
    totalGuaranteeRate: GUARANTEE_RATE,
    totalInstallment: loanFee,
    widgetConfig: {
      widgetVersion: DEFAULT_WIDGET_VERSION,
      widgetShowPreapproval: false,
    },
  };
}

export function getDefaultAllyConfig(country: string) {
  if (country === 'co') {
    return {
      minAmount: 50000,
      maxAmount: 2500000,
      isActiveAlly: true,
      policy: {
        discount: 0,
        productType: 'ADDI_FINANCIA',
        policyMaxAmount: 20000000,
      },
      widgetConfig: {
        widgetVersion: 'ADDI_TEMPLATE_01',
        widgetShowPreapproval: true,
      },
    };
  }

  return {
    minAmount: 50,
    maxAmount: 2000,
    isActiveAlly: true,
    policy: {
      discount: 0,
      productType: 'ADDI_PAGO',
      policyMaxAmount: 2000,
    },
    widgetConfig: {
      widgetVersion: 'ADDI_TEMPLATE_01',
      widgetShowPreapproval: true,
    },
  };
}

/**
 *
 * Numeric
 *
 */
export const formatCurrency = (value: number, format = 'de-DE', options = {}) =>
  value.toLocaleString(format, options);

/**
 *
 * Functional
 *
 */

export const dashToCamel = (str: string) =>
  str.replace(/\b-([a-z])/g, (_, char) => char.toUpperCase());

export const debounce = (fn: () => void = null, wait: number) => {
  let timeout: number;

  return () => {
    if (timeout) clearTimeout(timeout);
    timeout = window.setTimeout(fn, wait);
  };
};

export const generateRandomID = (): string =>
  `${Math.random().toString(36).substr(2, 9)}`;
